/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnDestroy, type OnInit, type SimpleChanges, Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStorageService } from '@dm/core';
import { ToolService } from 'app/common/services';
import {
    concat as _concat,
    debounce as _debounce,
    isArray as _isArray,
    isEmpty,
    isEqual as _isEqual,
    uniqWith as _uniqWith,
} from 'lodash';
import { type Subscription } from 'rxjs';

import { InfigoService } from '../global-infigo/infigo.service';
import { GlobalSearchService } from '../global-search.service';
import {
    type SearchData,
    type Tip,
    DEBOUNCE_TIME,
    DOWN_ARROW,
    ENTER,
    GlobalSearchStrService,
    GroupTypeE,
    HistoryTypeE,
    INCREMENT,
    UP_ARROW,
} from '../global-search-str.service';
import { SAN_OBJ_LIST } from './global-search-list.const';

@Component({
    selector: 'app-global-search-list',
    templateUrl: './global-search-list.component.html',
    styleUrls: ['./global-search-list.component.scss', '../global-search.component.scss'],
})
export class GlobalSearchListComponent implements OnInit, OnChanges, OnDestroy {
    // 源数据
    @Input() data: { [key: string]: SearchData[] } = {
        objects: [],
        operations: [],
        pages: [],
        helps: [],
    };
    @Input() baseCount = INCREMENT;
    @Input() more = false;
    @Input() inputValue = '';
    @Output() viewPageChange = new EventEmitter<boolean>();
    @Output() viewMoreClick = new EventEmitter();
    i18n = this.strService.i18n;
    detailTips: Tip[];
    showProduction = false;
    tipsSub$: Subscription;
    debouncedDetailHover: any;

    supportSmartThin = this.toolService.supportSmartThinLicense();

    constructor(
        public strService: GlobalSearchStrService,
        public service: GlobalSearchService,
        private router: Router,
        private route: ActivatedRoute,
        public infigoService: InfigoService,
        public sessionStorageService: SessionStorageService,
        private toolService: ToolService,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { data } = changes;
        if (data && !this.more) {
            this.setSelectionItem(false);
        }
    }

    ngOnInit(): void {
        this.debouncedDetailHover = _debounce(this.detailHover, DEBOUNCE_TIME);
        this.combineKeyup();
    }

    ngOnDestroy(): void {
        window.removeEventListener('keydown', this.keydownChangeItem);
        window.removeEventListener('keypress', this.keypressSelectItem);
    }

    /**
     * 跳转到搜索结果页面
     * @param result
     */
    viewResult(result: SearchData): void {
        this.clearParams();
        if (result.groupType === GroupTypeE.object) {
            this.sessionStorageService.set('vstoreId', isEmpty(result.vstoreId) ? '-1' : result.vstoreId);
            if (!this.supportSmartThin && SAN_OBJ_LIST.includes(result.type as string)) {
                return;
            }
        }
        // 清空路由操作是异步，保证路由清空
        setTimeout(() => {
            this.router.navigate([result.url], {
                queryParams: {
                    tab: result.tab,
                    subTab: result.subTab,
                    action: result.action,
                    actionParams: result.actionParams,
                },
            });
        });

        this.saveSearch(result);
        this.baseCount = INCREMENT;
        this.viewPageChange.emit(false);
    }

    viewHelp(result: { local: string }): void {
        const baseUrl = this.infigoService.getHelpDocUrl();
        const url = `${baseUrl}#${result.local}`;
        window.open(url, '_blank');
        this.viewPageChange.emit(false);
    }

    /**
     * 清空路由参数，解决关闭或tab切换时，没有删除tab或action参数，再次点击搜索结果，无法正常执行
     */
    clearParams(): void {
        const action = this.route.snapshot.queryParams.action;
        const tab = this.route.snapshot.queryParams.tab;
        if (action || tab) {
            this.router.navigate([], {
                queryParams: null,
            });
        }
    }

    /**
     * 查看object详情
     */
    showDetailTip(object: SearchData): void {
        this.debouncedDetailHover(object);
        this.detailTips = null;
    }

    // 拿到hover detail
    detailHover(object: SearchData): void {
        if (!object.tips) {
            // 保证当前流只有一个
            if (this.tipsSub$) {
                this.tipsSub$.unsubscribe();
            }
            this.tipsSub$ = this.service.dataMap[object.type].getCombinationObservable(object.actionParams).subscribe((result: Tip[]) => {
                object.tips = result;
                this.detailTips = object.tips;
            });
        } else {
            this.detailTips = object.tips;
        }
    }

    /**
     * 查看更多object
     */
    viewMore(): void {
        this.viewMoreClick.emit();
    }

    /**
     * 上下快捷键
     */
    setSelectionItem(moveup: boolean): void {
        const whole: SearchData[] = [];
        _concat(this.data.objects.slice(0, this.baseCount), this.data.operations, this.data.pages).forEach((item) => {
            whole.push(item);
        });

        const len = whole.length;
        if (!len) {
            return;
        }

        let index = whole.findIndex((item) => item.selected);
        if (index !== -1) {
            if (moveup) {
                whole[index--].selected = false;
                if (index < 0) {
                    index = len - 1;
                }
            } else {
                whole[index++].selected = false;
                if (index > len - 1) {
                    index = 0;
                }
            }
            whole[index].selected = true;
        } else {
            if (moveup) {
                whole[len - 1].selected = true;
            } else {
                whole[0].selected = true;
            }
        }

        // 保证选中渲染后设置滚动条位置
        setTimeout(() => {
            this.setSelectionItemPos();
        });
    }

    /**
     * 保存已查看的搜索结果
     */
    saveSearch(searchData: SearchData): void {
        if (searchData.groupType === GroupTypeE.page) {
            this.saveSearchHistory(searchData, HistoryTypeE.page);
        }
        if (searchData.groupType === GroupTypeE.operation) {
            this.saveSearchHistory(searchData, HistoryTypeE.operation);
        }
    }

    /**
     * 保存历史记录
     */
    saveSearchHistory(searchData: SearchData, historyType: HistoryTypeE): void {
        searchData.selected = false;
        let searchHistory = this.service.historyData[historyType];
        if (_isArray(searchHistory)) {
            searchHistory.unshift(searchData);
            searchHistory = _uniqWith(searchHistory, _isEqual);
            if (searchHistory.length > 3) {
                searchHistory.pop();
            }
        } else {
            searchHistory = [searchData];
        }
        this.service.historyData[historyType] = searchHistory;
    }

    /**
     * 快捷键
     */
    combineKeyup(): void {
        window.addEventListener('keydown', this.keydownChangeItem);
        window.addEventListener('keypress', this.keypressSelectItem);
    }

    /**
     * 键盘上下键切换对象
     */
    keydownChangeItem = (e: KeyboardEvent): void => {
        if (e.keyCode === DOWN_ARROW) {
            this.setSelectionItem(false);
        }
        if (e.keyCode === UP_ARROW) {
            this.setSelectionItem(true);
        }
    };

    /**
     * 回车键选中条目
     */
    keypressSelectItem = (e: KeyboardEvent): void => {
        if (e.keyCode === ENTER) {
            const result: SearchData = _concat(this.data.objects, this.data.operations, this.data.pages).find((item) => item.selected);
            if (result) {
                this.viewResult(result);
            }
        }
    };

    /**
     * 设置滚动条位置
     */
    private setSelectionItemPos(): void {
        const container = document.querySelector('.global-search-list-container');
        const el: HTMLElement = document.querySelector('.global-search-selected');
        if (!el) {
            return;
        }
        const offsetTop = el.offsetTop - 67;
        const scrollTop = container.scrollTop;
        if (offsetTop < scrollTop) {
            container.scrollTop = offsetTop;
        }
        if (offsetTop > scrollTop + container.clientHeight - el.offsetHeight) {
            container.scrollTop = offsetTop - (container.clientHeight - 67 + el.offsetHeight);
        }
    }
}
